import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import { colors } from '@/styles';
import { Signal } from '@/models/data/Signal';
import { TileType } from '@/models/TileTypes';
import { pixelRect } from '@/utils/tilePixels';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import { TileDefinition, TileGraphicsDefinition } from '@/models/tiles/TileDefinition';
import { Side, TileSideIOType } from '@/schemas/tiles';
import { bridgeBuildAndConnect } from '@/models/logic/utils';
import { Direction } from '@/utils/direction';
import { TileInstance } from '@/models/tiles/TileInstance';

export interface BridgeTileState {
  verticalNodeId?: NodeHandle;
  horizontalNodeId?: NodeHandle;
}

export class BridgeTile implements TileDefinition<BridgeTileState> {
  type = TileType.Bridge;

  name = 'Bridge';
  canRotate = false;

  createDefaultState = () => ({});
  buildAndConnectNodes = bridgeBuildAndConnect;

  getGraphBuildVisitKey = (instance: TileInstance, entryDirection?: Direction) => {
    if (entryDirection === undefined) return instance.id;
    return `${instance.id}:${entryDirection}`;
  };

  graphics = bridgeTileGraphicsDefinition;

  ioSides = {
    [Side.TOP]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
    [Side.RIGHT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
    [Side.BOTTOM]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
    [Side.LEFT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
  };
}

export const bridgeTileGraphicsDefinition: TileGraphicsDefinition<BridgeTileState> = {
  type: TileType.Bridge,
  draw: function (g, { meta, connections, context, state }): void {
    defaultTileGraphics(g);

    const ioSides = {
      [Side.TOP]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
      [Side.RIGHT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
      [Side.BOTTOM]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
      [Side.LEFT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
    };

    connectionGraphics(
      g,
      colors.signalColor,
      colors.noSignalColor,
      meta,
      connections,
      ioSides,
      context,
    );

    let isVerticalHigh = false;
    let isHorizontalHigh = false;

    if (state && context && context.mainChip) {
      const logicGraph = context.mainChip.logicCircuit.logicGraph;
      if (state.verticalNodeId) {
        const node = logicGraph.nodes.get(state.verticalNodeId);
        if (node?.output) isVerticalHigh = true;
      }
      if (state.horizontalNodeId) {
        const node = logicGraph.nodes.get(state.horizontalNodeId);
        if (node?.output) isHorizontalHigh = true;
      }
    } else {
      // Fallback to inputs if state not available (e.g. preview)
      isVerticalHigh = [meta.inputs.top, meta.inputs.bottom].some(
        (signal) => signal === Signal.HIGH,
      );

      isHorizontalHigh = [meta.inputs.left, meta.inputs.right].some(
        (signal) => signal === Signal.HIGH,
      );
    }

    pixelRect(g, 8, 0, 9, 25, isVerticalHigh ? colors.signalColor : colors.noSignalColor);
    // A shallow stepped arch keeps the two independent nets visibly separated.
    const lift = [
      0, 0, 0, 0, 1, 2, 3, 4, 5, 5, 5, 5, 5, 5, 5, 5, 5, 4, 3, 2, 1, 0, 0, 0, 0,
    ];
    const horizontalColor = isHorizontalHigh ? colors.signalColor : colors.noSignalColor;
    for (const [x, rise] of lift.entries()) {
      if (rise) {
        pixelRect(g, x, 8 - rise, 1, 9, colors.wireBorder);
        pixelRect(g, x, 10 - rise, 1, 5, horizontalColor);
      } else pixelRect(g, x, 8, 1, 9, horizontalColor);
    }
  },
};
